import React, { ReactElement, useEffect, useRef } from 'react';
import { BiSend } from 'react-icons/bi';
import { Card } from 'react-bootstrap';
import { LiveProps } from '../../LiveContainer';
import './LiveMessages.scss';
import { useState } from 'react';
import { RequestSendMessage } from '../../../../services/api/models/request/requestSendMessage';
import { useSendMessageMutation } from '../../../../services/api/apiProvider';
import { Debug } from '../../../../common/utils/debugLogs';
import { convertRtmMessage } from '../../models/liveComment';
import { LiveMessageItem } from './LiveMessageItem';
import PuffLoader from 'react-spinners/PuffLoader';
import { MessageType } from '../../types/messageType';
import { GiftMessage } from '../../models/giftMessage';
import { store } from '../../../../store/store';

export const LiveMessages = (props: LiveProps): ReactElement => {
  const [retryLoginCount, setRetryLoginCount] = useState(0);
  const [sendMessage] = useSendMessageMutation();
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const messageBoxRef = useRef<HTMLDivElement>(null);

  const liveAuthId = props.liveState.liveData?.id;

  function scrollListMessageToBottom() {
    // scroll
    messageBoxRef.current?.scrollTo({
      behavior: 'smooth',
      top: messageBoxRef.current.scrollHeight,
    });
  }

  const isPureEnter = (event: any): boolean => {
    const isComposing = event.isComposing || event.keyCode === 229;
    return event.key === 'Enter' && !isComposing;
  };

  const onTextBoxKeyDown = (event: any) => {
    if (isPureEnter(event)) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (
      props.liveState.isInLive &&
      props.liveState.liveData !== undefined &&
      props.liveState.userData !== undefined
    ) {
      props.liveState.rtmEngine
        ?.login(props.liveState.userData.id, props.liveState.liveData.id)
        .then(() => {
          if (props.liveState.liveData) {
            props.liveState.rtmEngine?.joinChannel(props.liveState.liveData.id);
          }
        })
        .catch(() => {
          Debug.log(
            `Rtm Login Fail --> Retry --> ${retryLoginCount} times in 3s`,
          );
          setTimeout(() => {
            setRetryLoginCount(retryLoginCount + 1);
          }, 3000);
        });

      props.liveState.rtmEngine?.on('ChannelMessage', (message: any): void => {
        const liveMessage = convertRtmMessage(JSON.stringify(message));
        if (liveAuthId !== liveMessage.liveId) {
          return;
        }
        if (
          liveMessage.commentConfig === MessageType.SEND_MESSAGE ||
          liveMessage.commentConfig === MessageType.JOIN_USER ||
          liveMessage.commentConfig === MessageType.FOLLOW_USER ||
          liveMessage.commentConfig === MessageType.LEAVE_USER ||
          liveMessage.commentConfig === MessageType.REQUEST_LIVE_MATCH ||
          liveMessage.commentConfig === MessageType.START_LIVE_MATCH
        ) {
          props.addLiveComment(liveMessage);
        }

        if (liveMessage.commentConfig === MessageType.SEND_NICE) {
          if (liveMessage.comment) {
            if (!liveMessage.comment.startsWith('いいね')) {
              props.addLiveComment(liveMessage);
            } else if (
              store.getState().live.userData?.id !== liveMessage.commentUser?.id
            ) {
              props.addLiveComment(liveMessage);
            }
          }
        }

        if (
          liveMessage.commentConfig === MessageType.SEND_GIFT &&
          liveMessage.comment !== undefined
        ) {
          props.addLiveComment({
            comment: (JSON.parse(liveMessage.comment) as GiftMessage).giftType
              ?.name,
            commentUser: liveMessage.commentUser,
            timestamp: liveMessage.timestamp,
            commentConfig: liveMessage.commentConfig,
          });
        }
      });

      props.liveState.rtmEngine?.on(
        'MessageFromPeer',
        (message: string): void => {
          Debug.log(JSON.stringify(message));
        },
      );
    }
  }, [props.liveState.isInLive]);

  const handleSendMessage = () => {
    const messageElement = document.querySelector('.live-messages__input_text');
    const message = messageElement?.textContent;

    if (
      messageElement === null ||
      message === null ||
      message === undefined ||
      message?.length === 0
    ) {
      return;
    }

    // send message to channel
    const requestSendMessage: RequestSendMessage = {
      message: message,
      type: 'Live',
      target: props.liveState.liveData?.id,
      id: null,
    };

    sendMessage(requestSendMessage)
      .unwrap()
      .catch((error) => {
        Debug.log(`Error when sent message to channel reason: ${error}`);
      });

    messageElement.textContent = '';
    setIsAutoScroll(true);
  };

  useEffect(() => {
    const messageBox = messageBoxRef.current;
    let prevScroll = messageBox?.scrollTop || 0;

    messageBox?.addEventListener('scroll', () => {
      const { scrollHeight, clientHeight, scrollTop } = messageBox;

      if (prevScroll > scrollTop) {
        // handle when scrolling back
        setIsAutoScroll(false);
      } else if (scrollTop === scrollHeight - clientHeight) {
        // handle when scrolling to the end of the comments
        setIsAutoScroll(true);
        setShowScrollBottom(false);
      }

      prevScroll = messageBox.scrollTop;
    });
  }, [messageBoxRef.current]);

  // not join or start live yet!
  const isReady =
    props.liveState.isInLive &&
    props.liveState.liveData !== undefined &&
    props.liveState.userData !== undefined;

  return (
    <div className="live-messages">
      <Card className="live-messages__content box-shadow-3">
        {/* Show scroll to bottom */}
        {showScrollBottom ? (
          <span
            className="live-messages__scroll-to-bottom"
            onClick={scrollListMessageToBottom}
          >
            新しいコメントを見る ↓
          </span>
        ) : (
          <></>
        )}

        {!isReady && !props.liveState.isEnded ? (
          <Card.Body
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <PuffLoader size={'60px'} color={'#0275d8'}></PuffLoader>
          </Card.Body>
        ) : (
          <Card.Body
            style={{ padding: '0px' }}
            className="live-messages__messages_list"
            ref={messageBoxRef}
          >
            {props.liveState.liveComments.map((comment, index) => {
              return (
                <div key={index}>
                  <LiveMessageItem
                    liveComment={comment}
                    setShowScrollBottom={setShowScrollBottom}
                    isAutoScroll={isAutoScroll}
                    scrollListMessageToBottom={scrollListMessageToBottom}
                  ></LiveMessageItem>
                </div>
              );
            })}
          </Card.Body>
        )}

        <div className="live-messages__input">
          <div
            className="live-messages__input_text"
            placeholder="コメントしてみましょう"
            contentEditable="true"
            onKeyDown={(event) => onTextBoxKeyDown(event)}
          ></div>
          <BiSend
            className="live-messages__input__send-btn"
            onClick={handleSendMessage}
          />
        </div>
      </Card>
    </div>
  );
};
